'use client'

import { useState } from 'react'
import { usePathname } from 'next/navigation'
import { AnimatePresence, motion } from 'framer-motion'
import { REGEXP_ONLY_DIGITS } from 'input-otp'
import { ArrowLeft, ArrowRight, CheckCircle2, Eye, EyeOff, Loader2, RotateCcw } from 'lucide-react'
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp'
import { useForgotPasswordFlow } from '@/lib/commerce/hooks'
import { navigateUrl } from '@/utils/client-request'

const inputStyle: React.CSSProperties = {
  background: 'rgba(255,255,255,0.035)',
  border: '1px solid rgba(255,255,255,0.07)',
  color: 'white',
}

const labelClass = 'block text-[11px] font-semibold uppercase tracking-[0.18em] mb-2'
const labelStyle: React.CSSProperties = { color: 'rgba(255,255,255,0.7)' }

const secondaryBtn: React.CSSProperties = {
  background: 'rgba(255,255,255,0.035)',
  border: '1px solid rgba(255,255,255,0.07)',
  color: 'white',
}

export default function Forgot({ vendor }: { vendor: any }) {
  const pathname = usePathname()
  const f = useForgotPasswordFlow(vendor)
  const [showPassword, setShowPassword] = useState(false)
  const [showPasswordRep, setShowPasswordRep] = useState(false)

  const handleKeyDown = (e: any) => { if (e.key === 'Enter') f.sendOTP() }

  return (
    <div className="w-full">
      <div className="text-[10px] font-semibold uppercase tracking-[0.22em]" style={{ color: '#a8a8b3' }}>Нууц үг сэргээх</div>
      <h1
        className="mt-3 font-semibold tracking-[-0.025em] leading-[0.95] text-white"
        style={{ fontFamily: 'Gilroy, Inter, sans-serif', fontSize: 'clamp(30px, 4vw, 38px)' }}
      >
        Мартсан байна уу?
      </h1>

      <AnimatePresence mode="wait">
        {(f.step === 3 && f.sentOTP && f.verificationId) ? (
          <motion.div key="reset" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.3 }} className="mt-8">
            <form onSubmit={f.handleResetPassword} className="space-y-4">
              <div>
                <label className={labelClass} style={labelStyle}>Шинэ нууц үг</label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Хамгийн багадаа 8 тэмдэгт"
                    onChange={(e) => f.setPassword(e.target.value)}
                    className="w-full h-12 rounded-full pl-5 pr-12 text-[13.5px] outline-none focus:ring-0 placeholder:text-white/30"
                    style={inputStyle}
                  />
                  <button type="button" onClick={() => setShowPassword(s => !s)} aria-label="Toggle" className="absolute right-4 top-1/2 -translate-y-1/2 hover:text-white" style={{ color: 'rgba(255,255,255,0.5)' }}>
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <div>
                <label className={labelClass} style={labelStyle}>Шинэ нууц үг давтах</label>
                <div className="relative">
                  <input
                    type={showPasswordRep ? 'text' : 'password'}
                    placeholder="Шинэ нууц үг давтах"
                    onChange={(e) => f.setPasswordRep(e.target.value)}
                    className="w-full h-12 rounded-full pl-5 pr-12 text-[13.5px] outline-none focus:ring-0 placeholder:text-white/30"
                    style={inputStyle}
                  />
                  <button type="button" onClick={() => setShowPasswordRep(s => !s)} aria-label="Toggle" className="absolute right-4 top-1/2 -translate-y-1/2 hover:text-white" style={{ color: 'rgba(255,255,255,0.5)' }}>
                    {showPasswordRep ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <button
                type="submit"
                disabled={f.loading}
                className="w-full h-12 rounded-full text-[14px] font-medium inline-flex items-center justify-center gap-2 transition-opacity disabled:opacity-60"
                style={{ background: 'white', color: '#0a0a0c' }}
              >
                {f.loading ? <><Loader2 className="h-4 w-4 animate-spin" /> Шинэчилж байна...</> : <>Нууц үг солих <ArrowRight className="h-3.5 w-3.5" /></>}
              </button>
            </form>
          </motion.div>
        ) : (f.step === 2 && f.sentOTP) ? (
          <motion.div key="otp" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.3 }} className="mt-8">
            <p className="text-[13.5px] leading-6 mb-6" style={{ color: 'rgba(255,255,255,0.6)' }}>
              Таны <span className="font-semibold text-white">{f.identifier}</span> руу илгээгдсэн 6 оронтой кодыг оруулна уу.
            </p>
            <div className="flex justify-center">
              {/* @ts-ignore */}
              <InputOTP value={f.otp} onChange={f.setOTP} autoFocus maxLength={6} pattern={REGEXP_ONLY_DIGITS} autoComplete="false" containerClassName="gap-2">
                {/* @ts-ignore */}
                <InputOTPGroup className="gap-2">
                  {/* @ts-ignore */}
                  <InputOTPSlot index={0} showChar className="h-12! w-10! sm:w-12! rounded-xl! border-l! text-white! text-base! font-semibold! bg-white/[0.035]! border-white/10!" />
                  {/* @ts-ignore */}
                  <InputOTPSlot index={1} showChar className="h-12! w-10! sm:w-12! rounded-xl! border-l! text-white! text-base! font-semibold! bg-white/[0.035]! border-white/10!" />
                  {/* @ts-ignore */}
                  <InputOTPSlot index={2} showChar className="h-12! w-10! sm:w-12! rounded-xl! border-l! text-white! text-base! font-semibold! bg-white/[0.035]! border-white/10!" />
                  {/* @ts-ignore */}
                  <InputOTPSlot index={3} showChar className="h-12! w-10! sm:w-12! rounded-xl! border-l! text-white! text-base! font-semibold! bg-white/[0.035]! border-white/10!" />
                  {/* @ts-ignore */}
                  <InputOTPSlot index={4} showChar className="h-12! w-10! sm:w-12! rounded-xl! border-l! text-white! text-base! font-semibold! bg-white/[0.035]! border-white/10!" />
                  {/* @ts-ignore */}
                  <InputOTPSlot index={5} showChar className="h-12! w-10! sm:w-12! rounded-xl! border-l! text-white! text-base! font-semibold! bg-white/[0.035]! border-white/10!" />
                </InputOTPGroup>
              </InputOTP>
            </div>
            <div className="mt-6 space-y-2">
              <button
                type="button"
                onClick={f.handleOTP}
                disabled={f.isOTPLoading}
                className="w-full h-12 rounded-full text-[14px] font-medium inline-flex items-center justify-center gap-2 transition-opacity disabled:opacity-60"
                style={{ background: 'white', color: '#0a0a0c' }}
              >
                {f.isOTPLoading ? <><Loader2 className="h-4 w-4 animate-spin" /> Баталгаажуулж байна...</> : <><CheckCircle2 className="h-4 w-4" /> Баталгаажуулах</>}
              </button>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={f.reset}
                  className="h-11 rounded-full text-[13px] font-medium inline-flex items-center justify-center gap-1.5 transition-colors hover:bg-white/10"
                  style={secondaryBtn}
                >
                  <ArrowLeft className="h-3.5 w-3.5" /> Буцах
                </button>
                <button
                  type="button"
                  onClick={f.resendOTP}
                  disabled={f.resendLoading || f.isCooldownActive}
                  className="h-11 rounded-full text-[13px] font-medium inline-flex items-center justify-center gap-1.5 transition-colors hover:bg-white/10 disabled:opacity-60 disabled:hover:bg-transparent"
                  style={secondaryBtn}
                >
                  {f.resendLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5" />}
                  {f.isCooldownActive ? `${f.cooldownSeconds}с` : 'Дахин илгээх'}
                </button>
              </div>
            </div>
          </motion.div>
        ) : (
          <motion.div key="send" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.3 }} className="mt-8">
            <p className="text-[13.5px] leading-6 mb-6" style={{ color: 'rgba(255,255,255,0.6)' }}>
              Бүртгэлтэй имэйл хаяг эсвэл утасны дугаараа оруулаарай. Бид баталгаажуулах кодыг тань руу илгээнэ.
            </p>
            <div className="flex items-center gap-1 p-1 rounded-full mb-4" style={{ background: 'rgba(255,255,255,0.035)', border: '1px solid rgba(255,255,255,0.07)' }}>
              {(['phone', 'email'] as const).map(t => {
                const active = f.tab === t
                return (
                  <button
                    key={t}
                    type="button"
                    onClick={() => f.setTab(t)}
                    className="flex-1 h-9 rounded-full text-[12.5px] font-medium transition-colors"
                    style={{
                      background: active ? 'white' : 'transparent',
                      color: active ? '#0a0a0c' : 'rgba(255,255,255,0.7)',
                    }}
                  >
                    {t === 'phone' ? 'Утасны дугаар' : 'Имэйл хаяг'}
                  </button>
                )
              })}
            </div>
            <div className="space-y-4">
              {f.tab === 'phone' ? (
                <div>
                  <label className={labelClass} style={labelStyle}>Утасны дугаар</label>
                  <input
                    value={f.phone}
                    onChange={(e) => f.setPhone(e.target.value)}
                    onKeyDown={handleKeyDown}
                    type="tel"
                    placeholder="00000000"
                    className="w-full h-12 rounded-full px-5 text-[13.5px] tabular-nums outline-none focus:ring-0 placeholder:text-white/30"
                    style={inputStyle}
                  />
                </div>
              ) : (
                <div>
                  <label className={labelClass} style={labelStyle}>Имэйл хаяг</label>
                  <input
                    value={f.email}
                    onChange={(e) => f.setEmail(e.target.value)}
                    onKeyDown={handleKeyDown}
                    type="email"
                    placeholder="mail@shop.mn"
                    className="w-full h-12 rounded-full px-5 text-[13.5px] outline-none focus:ring-0 placeholder:text-white/30"
                    style={inputStyle}
                  />
                </div>
              )}
              <button
                type="button"
                onClick={f.sendOTP}
                disabled={f.sendOTPLoading}
                className="w-full h-12 rounded-full text-[14px] font-medium inline-flex items-center justify-center gap-2 transition-opacity disabled:opacity-60"
                style={{ background: 'white', color: '#0a0a0c' }}
              >
                {f.sendOTPLoading ? <><Loader2 className="h-4 w-4 animate-spin" /> Илгээж байна...</> : <>Код илгээх <ArrowRight className="h-3.5 w-3.5" /></>}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <p className="mt-7 text-center text-[13px]" style={{ color: 'rgba(255,255,255,0.6)' }}>
        Нууц үгээ санасан уу?{' '}
        <a href={navigateUrl('/login', pathname)} className="text-white font-medium underline underline-offset-2 hover:opacity-80 transition-opacity">
          Нэвтрэх
        </a>
      </p>
    </div>
  )
}
